import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";

const WINDOWS = [
    { id: "1h", label: "1H" },
    { id: "24h", label: "24H" },
    { id: "7d", label: "7D" },
    { id: "30d", label: "30D" },
];

const FLAGS = {
    "United States": "🇺🇸", "United Arab Emirates": "🇦🇪", "India": "🇮🇳",
    "United Kingdom": "🇬🇧", "Saudi Arabia": "🇸🇦", "Pakistan": "🇵🇰",
    "Egypt": "🇪🇬", "Germany": "🇩🇪", "France": "🇫🇷", "Canada": "🇨🇦",
    "Australia": "🇦🇺", "Singapore": "🇸🇬", "Philippines": "🇵🇭",
};

function timeAgo(iso) {
    if (!iso) return "";
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 60) return `${Math.round(s)}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
}

export default function AdminVisitors() {
    const [window, setWindow] = useState("24h");
    const [stats, setStats] = useState(null);
    const [live, setLive] = useState(null);
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState({ event_type: "", country: "" });
    const [loading, setLoading] = useState(true);

    const loadStats = useCallback(async () => {
        try {
            const { data } = await api.get(`/admin/visits/stats?window=${window}`);
            setStats(data);
        } catch (_) { /* noop */ }
    }, [window]);

    const loadLive = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/visits/live");
            setLive(data);
        } catch (_) { /* noop */ }
    }, []);

    const loadList = useCallback(async () => {
        try {
            const params = new URLSearchParams({ limit: "50" });
            if (filter.event_type) params.set("event_type", filter.event_type);
            if (filter.country) params.set("country", filter.country);
            const { data } = await api.get(`/admin/visits?${params.toString()}`);
            setItems(data.items);
        } catch (_) { /* noop */ }
    }, [filter]);

    const exportCsv = useCallback(async () => {
        try {
            const params = new URLSearchParams({ limit: "10000" });
            if (filter.event_type) params.set("event_type", filter.event_type);
            if (filter.country) params.set("country", filter.country);
            const { data } = await api.get(
                `/admin/visits/export.csv?${params.toString()}`,
                { responseType: "blob" },
            );
            const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
            a.href = url;
            a.download = `visitors-${stamp}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (_) { /* noop */ }
    }, [filter]);

    useEffect(() => {
        setLoading(true);
        Promise.all([loadStats(), loadLive(), loadList()]).finally(() => setLoading(false));
        const t = setInterval(() => { loadLive(); loadStats(); }, 15_000);
        return () => clearInterval(t);
    }, [loadStats, loadLive, loadList]);

    const KPI = ({ label, value, sub, accent }) => (
        <div className="lx-card-dark p-5">
            <div className="lx-label text-[#D4AF37]">{label}</div>
            <div className="font-serif text-4xl text-white mt-2" style={accent ? { color: "#D4AF37" } : {}}>{value}</div>
            {sub && <div className="text-xs text-white/40 mt-1">{sub}</div>}
        </div>
    );

    const Bars = ({ data = [], unit = "", label }) => {
        const max = Math.max(1, ...data.map((d) => d.count));
        return (
            <div className="lx-card-dark p-5">
                <div className="lx-label text-[#D4AF37] mb-3">{label}</div>
                {data.length === 0 && (
                    <div className="text-xs text-white/40 py-4 text-center">No data yet</div>
                )}
                <div className="space-y-2">
                    {data.slice(0, 10).map((d) => (
                        <div key={d.label} className="text-xs">
                            <div className="flex justify-between text-white/80 mb-1">
                                <span className="truncate pr-2">{d.label}</span>
                                <span className="text-[#D4AF37] tabular-nums">{d.count}{unit}</span>
                            </div>
                            <div className="h-1.5 bg-[#2B1438] rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#E8D399]" style={{ width: `${(d.count / max) * 100}%` }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const series = stats?.series || [];
    const seriesMax = Math.max(1, ...series.map((p) => p.count));

    return (
        <div className="space-y-6">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="lx-label text-[#D4AF37] mb-2">Analytics</div>
                    <h1 className="font-serif text-4xl text-white">Visitors</h1>
                </div>
                <div className="flex gap-1 border border-[#2B1438] rounded">
                    {WINDOWS.map((w) => (
                        <button
                            key={w.id}
                            onClick={() => setWindow(w.id)}
                            data-testid={`visitors-window-${w.id}`}
                            className={`px-4 py-2 text-xs uppercase tracking-wider ${
                                window === w.id ? "bg-[#D4AF37] text-[#180A21]" : "text-white/60"
                            }`}
                        >
                            {w.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <KPI label="Online now" value={live?.online_now ?? "—"} sub="Last 5 minutes" accent />
                <KPI label="Total visits" value={(stats?.total_visits ?? 0).toLocaleString()} sub={`Window: ${window}`} />
                <KPI label="Unique sessions" value={(stats?.unique_sessions ?? 0).toLocaleString()} />
                <KPI label="Signed-in users" value={(stats?.unique_users ?? 0).toLocaleString()} sub="Distinct accounts" />
            </div>

            {/* Trend chart */}
            <div className="lx-card-dark p-5">
                <div className="lx-label text-[#D4AF37] mb-4">Trend</div>
                <div className="flex items-end gap-1 h-32">
                    {series.map((p, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${p.count} visits @ ${new Date(p.t).toLocaleString()}`}>
                            <div
                                className="w-full bg-gradient-to-t from-[#D4AF37] to-[#E8D399] rounded-t"
                                style={{ height: `${(p.count / seriesMax) * 100}%`, minHeight: 2 }}
                            />
                        </div>
                    ))}
                </div>
                <div className="flex justify-between text-[10px] text-white/40 mt-2">
                    <span>{stats?.since ? new Date(stats.since).toLocaleString() : ""}</span>
                    <span>Now</span>
                </div>
            </div>

            {/* Funnel */}
            <div className="lx-card-dark p-5">
                <div className="lx-label text-[#D4AF37] mb-4">Conversion funnel</div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {[
                        ["Visits", stats?.funnel?.visits],
                        ["Logins", stats?.funnel?.logins],
                        ["Sign-ups", stats?.funnel?.registrations],
                        ["Bookings", stats?.funnel?.bookings],
                        ["Payments", stats?.funnel?.payments],
                    ].map(([label, count], i) => (
                        <div key={label} className="text-center p-3 bg-[#0F0614] border border-[#2B1438]">
                            <div className="text-xs text-white/60">{label}</div>
                            <div className="font-serif text-2xl text-[#D4AF37] mt-1">{(count ?? 0).toLocaleString()}</div>
                            {i > 0 && (stats?.funnel?.visits || 0) > 0 && (
                                <div className="text-[10px] text-white/40 mt-1">
                                    {((100 * (count || 0)) / stats.funnel.visits).toFixed(1)}%
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Breakdown grid */}
            <div className="grid lg:grid-cols-3 gap-3">
                <Bars data={stats?.top_pages} label="Top pages" />
                <Bars data={stats?.top_referrers} label="Top referrers" />
                <Bars data={stats?.by_country} label="By country" />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                <Bars data={stats?.by_device} label="Device" />
                <Bars data={stats?.by_browser} label="Browser" />
            </div>

            {/* Live sessions */}
            <div className="lx-card-dark p-5">
                <div className="flex items-center justify-between mb-3">
                    <div className="lx-label text-[#D4AF37]">Live sessions</div>
                    <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                        <span className="text-xs text-white/40">Auto-refresh 15s</span>
                    </div>
                </div>
                {(live?.sessions || []).length === 0 && (
                    <div className="text-xs text-white/40 py-6 text-center">No one online right now.</div>
                )}
                <div className="space-y-2">
                    {(live?.sessions || []).slice(0, 20).map((s) => (
                        <div key={s.session_id} className="flex items-center justify-between border border-[#2B1438] bg-[#0F0614] px-3 py-2" data-testid={`live-${s.session_id.slice(0, 8)}`}>
                            <div className="flex items-center gap-3 min-w-0">
                                <span>{FLAGS[s.country] || "🌍"}</span>
                                <div className="min-w-0">
                                    <div className="text-sm text-white truncate">{s.user_name || "Anonymous"} <span className="text-white/40 text-xs">· {s.path}</span></div>
                                    <div className="text-[10px] text-white/40">{s.city || s.country || "—"} · {s.device} · {s.browser}{s.user_role ? ` · ${s.user_role}` : ""}</div>
                                </div>
                            </div>
                            <div className="text-[10px] text-white/40 shrink-0">{timeAgo(s.last_seen)} · {s.events} hit{s.events > 1 ? "s" : ""}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Recent visits table */}
            <div className="lx-card-dark p-5">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="lx-label text-[#D4AF37]">Recent visits</div>
                    <div className="flex gap-2 flex-wrap">
                        <select
                            value={filter.event_type}
                            onChange={(e) => setFilter({ ...filter, event_type: e.target.value })}
                            data-testid="visitors-filter-event"
                            className="px-2 py-1 bg-[#0F0614] border border-[#2B1438] text-white text-xs"
                        >
                            <option value="">All events</option>
                            <option value="pageview">Page view</option>
                            <option value="signup">Sign-up</option>
                            <option value="login">Login</option>
                            <option value="booking_created">Booking</option>
                            <option value="payment_completed">Payment</option>
                        </select>
                        <select
                            value={filter.country}
                            onChange={(e) => setFilter({ ...filter, country: e.target.value })}
                            data-testid="visitors-filter-country"
                            className="px-2 py-1 bg-[#0F0614] border border-[#2B1438] text-white text-xs max-w-[180px]"
                        >
                            <option value="">All countries</option>
                            {(stats?.by_country || []).map((c) => (
                                <option key={c.label} value={c.label}>{c.label} ({c.count})</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            data-testid="visitors-export-csv"
                            onClick={exportCsv}
                            className="px-3 py-1 bg-[#D4AF37] hover:bg-[#E8D399] text-[#180A21] text-xs uppercase tracking-wider font-medium transition-colors"
                        >
                            Export CSV
                        </button>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-white/40 uppercase tracking-wider text-[10px]">
                                <th className="text-left py-2 pr-3">Time</th>
                                <th className="text-left py-2 pr-3">User</th>
                                <th className="text-left py-2 pr-3">Event</th>
                                <th className="text-left py-2 pr-3">Path</th>
                                <th className="text-left py-2 pr-3">From</th>
                                <th className="text-left py-2 pr-3">Location</th>
                                <th className="text-left py-2 pr-3">Device</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && !loading && (
                                <tr><td colSpan={7} className="text-center text-white/40 py-6">No visits yet.</td></tr>
                            )}
                            {items.map((v) => (
                                <tr key={v.id} className="border-t border-[#2B1438]/60">
                                    <td className="py-2 pr-3 text-white/60">{timeAgo(v.created_at)}</td>
                                    <td className="py-2 pr-3 text-white">{v.user_name || <span className="text-white/40">Anon</span>}</td>
                                    <td className="py-2 pr-3">
                                        <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm ${
                                            v.event_type === "payment_completed" ? "bg-emerald-500/20 text-emerald-300" :
                                            v.event_type === "booking_created" ? "bg-[#D4AF37]/20 text-[#D4AF37]" :
                                            v.event_type === "signup" ? "bg-blue-500/20 text-blue-300" :
                                            v.event_type === "login" ? "bg-purple-500/20 text-purple-300" :
                                            "bg-[#2B1438] text-white/60"
                                        }`}>{v.event_type}</span>
                                    </td>
                                    <td className="py-2 pr-3 text-white/80 truncate max-w-[220px]">{v.path}</td>
                                    <td className="py-2 pr-3 text-white/40 truncate max-w-[180px]">{v.referrer || "—"}</td>
                                    <td className="py-2 pr-3 text-white/60">{FLAGS[v.country] || ""} {v.city ? `${v.city}, ${v.country_code || ""}` : v.country || "—"}</td>
                                    <td className="py-2 pr-3 text-white/40">{v.device} · {v.browser}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="text-xs text-white/40">
                Privacy: raw IPs are never stored. We keep a salted hash for unique-visitor math only.
            </div>
        </div>
    );
}
